import { useState } from "react";

import { makePostApiCall, makePutApiCall } from "../../../shared/makeApiCall";
import { useToast } from "../../../contexts/ToastContext";

import DeleteConfirmationForm from "../../../components/forms/DeleteConfirmationForm";
import ModelErrorComponent from "../../../components/modals/ModelErrorComponent";
import type { EmptyModalState } from "../../../components/modals/EmptyModal";
import type { BreadcrumbPage } from "../../../components/controls/BreadCrumbs";
import type { SelectOption } from "../../../types/api";
import type { CategoryMutationResponse, SubCategoryFormValues, SubCategorySummary } from "../../../types/categories";

export function SubCategoryDetailsViewModel(logout: () => void, categoryId?: string) {
  const { showSuccess } = useToast();
  const [emptyModal, setEmptyModelOpen] = useState<EmptyModalState>({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState<SubCategorySummary | null>(null);
  const [primaryCategories, setPrimaryCategories] = useState<SelectOption[]>([]);

  const [breadCrumbs, setBreadCrumbs] = useState<BreadcrumbPage[]>([
    {
      name: "Sub Categories",
      href: "/sub-categories",
      current: false,
    },
    { name: "Create New", href: "#", current: true },
  ]);

  const showErrorModal = (title: string, subtitle = "Please try again.") => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent setOpen={setEmptyModelOpen} subtitle={subtitle} title={title} />
      ),
      open: true,
    });
  };

  const getCategory = async function (search: string | null = null, page = 1) {
    setLoading(true);
    const payload = {
      page: page,
      search: search,
    };
    const callResult = await makePostApiCall<SubCategorySummary, typeof payload>(`products/subcategories/${categoryId}`, payload);
    if (callResult.success) {
      setCategory(callResult.data);
      setBreadCrumbs([
        {
          name: "Sub Categories",
          href: "/sub-categories",
          current: false,
        },
        { name: callResult.data.name, href: "#", current: true },
      ]);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch category");
    }
    setLoading(false);
  };

  const createCategory = async function (values: SubCategoryFormValues) {
    const callResult = await makePostApiCall<CategoryMutationResponse, SubCategoryFormValues>(
      `products/subcategories/create`,
      values,
      "multipart/form-data"
    );

    if (callResult.success) {
      showSuccess(callResult.data.message || "Action completed");
      window.location.assign("/sub-categories/" + callResult.data.id);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not create category");
    }
  };

  const updateCategory = async function (values: SubCategoryFormValues) {
    const callResult = await makePostApiCall<CategoryMutationResponse, SubCategoryFormValues>(
      `updateSubCategory/${categoryId}`,
      values,
      "multipart/form-data"
    );

    if (callResult.success) {
      showSuccess(callResult.data.message || "Action completed");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not update category");
    }
  };

  const getPrimaryCategories = async function (search: string | null = null, page = 1) {
    const payload = {
      page: page,
      search: search,
    };
    const callResult = await makePostApiCall<SelectOption[], typeof payload>(`/products/categories/dropdown`, payload);
    if (callResult.success) {
      setPrimaryCategories(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch categories");
    }
  };

  return {
    createCategory,
    updateCategory,
    getCategory,
    category,
    loading,
    emptyModal,
    getPrimaryCategories,
    primaryCategories,
    breadCrumbs,
  };
}
